import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "@iq/models";
import { deleteProduct } from "../../services/productService";
import { fetchInventory } from "../../services/inventoryService";

function DeleteProduct({ isOpen, onClose, productId, productName = "", onDeleteSuccess }) {
    const [inventoryRows, setInventoryRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!isOpen || !productId) {
            setInventoryRows([]);
            setErrorMessage("");
            return;
        }

        let ignore = false;

        async function loadInventoryRows() {
            setLoading(true);
            setErrorMessage("");

            try {
                const payload = await fetchInventory();
                const rows = Array.isArray(payload?.inventory) ? payload.inventory : [];
                const matchingRows = rows.filter((item) => Number(item?.product?.id ?? item?.product_id ?? 0) === Number(productId));

                if (!ignore) {
                    setInventoryRows(matchingRows);
                }
            } catch (error) {
                if (!ignore) {
                    setInventoryRows([]);
                    setErrorMessage(error?.message || "Unable to load product inventory.");
                }
            } finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        }

        loadInventoryRows();

        return () => {
            ignore = true;
        };
    }, [isOpen, productId]);

    const totalRemainingStock = useMemo(
        () => inventoryRows.reduce((total, row) => total + Number(row?.stock ?? row?.Stock ?? 0), 0),
        [inventoryRows]
    );

    const hasRemainingStock = totalRemainingStock > 0;

    async function handleDelete() {
        if (hasRemainingStock) {
            setErrorMessage("This product still has inventory on hand. Reduce stock to zero before deleting.");
            return;
        }

        setIsDeleting(true);
        setErrorMessage("");

        try {
            await deleteProduct(productId);
            onDeleteSuccess?.();
            onClose();
        } catch (error) {
            setErrorMessage(error?.message || "Failed to delete product.");
        } finally {
            setIsDeleting(false);
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Delete Product">
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <div>
                    <p style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>Delete: {productName}</p>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {loading ? (
                        <p style={{ margin: 0, color: "#666" }}>Checking inventory...</p>
                    ) : inventoryRows.length > 0 ? (
                        <>
                            <p style={{ margin: 0, fontWeight: 600 }}>
                                {hasRemainingStock ? "Inventory still on hand:" : "Zero-stock inventory rows:"}
                            </p>
                            <div style={{ display: "grid", gap: "0.35rem", maxHeight: "220px", overflowY: "auto" }}>
                                {inventoryRows.map((row, index) => {
                                    const sku = row?.product_variant?.sku ?? row?.sku ?? `Variant ${index + 1}`;
                                    const stock = Number(row?.stock ?? row?.Stock ?? 0);

                                    return (
                                        <div
                                            key={`${sku}-${index}`}
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                gap: "0.75rem",
                                                padding: "0.5rem 0.75rem",
                                                borderRadius: "8px",
                                                background: "rgba(0,0,0,0.04)",
                                            }}
                                        >
                                            <span>{sku}</span>
                                            <strong>{stock}</strong>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <p style={{ margin: 0, color: "#666" }}>
                            No active inventory rows were found for this product.
                        </p>
                    )}
                </div>

                {hasRemainingStock ? (
                    <p style={{ margin: 0, color: "#b42318", fontWeight: 600 }}>
                        This product still has inventory remaining. Clear the stock first, then delete it.
                    </p>
                ) : (
                    <p style={{ margin: 0, color: "#c2c7ce" }}>
                        No stock remains in the database. Do you want to delete this product and its remaining zero-stock records too?
                    </p>
                )}

                {errorMessage ? <p style={{ margin: 0, color: "#b42318" }}>{errorMessage}</p> : null}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
                    <button type="button" onClick={onClose} style={{ padding: "0.7rem 1rem", borderRadius: "8px", border: "1px solid #d0d5dd", background: "#fff", cursor: "pointer" }}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={hasRemainingStock || isDeleting || loading}
                        style={{
                            padding: "0.7rem 1rem",
                            borderRadius: "8px",
                            border: "none",
                            background: hasRemainingStock ? "#d0d5dd" : "#b42318",
                            color: "#fff",
                            cursor: hasRemainingStock || isDeleting || loading ? "not-allowed" : "pointer",
                        }}
                    >
                        {isDeleting ? "Deleting..." : hasRemainingStock ? "Delete Product" : "Confirm Delete"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default DeleteProduct;